// Schedule, change or cancel a trip's daily weather email -- and send one now.
//
// A bespoke action dialog, like ShareTripModal and RenameTripModal, not an
// autosaved EditForm: "Schedule" is a decision, not a keystroke, and there is
// nothing here worth writing per character. Errors show inline because the
// page's status bar is behind the modal, the same arrangement those two use.
//
// Re-queried on every open rather than cached, for ShareTripModal's reasons:
// another tab may have changed it, and a rename can move the group key
// underneath us.
//
// TWO THINGS THAT LOOK LIKE MISTAKES AND ARE NOT
//
// 1. The hour picker does NOT reuse EmailSubscriptionsCard's localHourLabel /
//    localOrderGmtHours. Those translate a stored GMT hour for display and
//    reorder the list so it reads in local order. This model stores a LOCAL
//    hour, so the labels are direct and there is nothing to reorder -- reusing
//    them would silently offset the picker by the viewer's own offset.
//
// 2. The date fields are native <input type="date">, not TaskPogoDatePicker.
//    Their value is already a bare 'YYYY-MM-DD' string, which is exactly what
//    the model stores; a Date-based picker would mean converting an instant to
//    a calendar day and back, which is the bug TripWeatherSchedule.mjs is
//    arranged to avoid.
//
// AND ONE RULE: Send now never saves the form. It is offered as a test send,
// and having it quietly create a subscription nobody asked for would be the
// opposite of what the button says.
//
// Send now QUEUES the email rather than sending it inline, so it answers in
// about a second and the notice says "Queued", not "Sent" -- the mail itself
// arriving is the confirmation. Everything the modal could report on is still
// checked before the dispatch and still comes back as an error here: the caller
// owning the trip, the trip resolving, the address being usable. What is not
// reported is a failure of the send ITSELF, which is now a CloudWatch log and
// a dead-letter message. See the Lambda's header for why the wait went away.

import { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import { extractErrorText, randomId } from '../../fleet-shared/Common.mjs';
import {
  DEFAULT_SEND_HOUR,
  defaultSendWindow,
  describeSchedule,
  describeDay,
  formatLocalHour,
  detectTimeZone,
} from '../../fleet-shared/TripWeatherSchedule.mjs';
import {
  getTripWeatherSubscription,
  createTripWeatherSubscription,
  updateTripWeatherSubscription,
  deleteTripWeatherSubscription,
  sendTripWeatherEmailNow,
} from '../../utilities/Database.mjs';

const stateLoading = 'loading';
const stateReady = 'ready';
const stateFailed = 'failed';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const WeatherEmailModal = ({ show, itemId, groupKey, group, defaultEmail, onHide }) => {
  const [loadState, setLoadState] = useState(stateLoading);
  const [subscription, setSubscription] = useState(null);
  const [email, setEmail] = useState('');
  const [sendHour, setSendHour] = useState(DEFAULT_SEND_HOUR);
  const [startDate, setStartDate] = useState('');
  const [stopDate, setStopDate] = useState('');
  const [timeZone, setTimeZone] = useState(detectTimeZone());
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const seedFrom = useCallback(
    (row) => {
      const window = defaultSendWindow(group);
      setSubscription(row);
      setEmail(row?.email ?? defaultEmail ?? '');
      setSendHour(row?.sendHourLocal ?? DEFAULT_SEND_HOUR);
      setStartDate(row?.startDate ?? window.startDate ?? '');
      setStopDate(row?.stopDate ?? window.stopDate ?? '');
      setTimeZone(row?.timeZone || detectTimeZone());
    },
    [group, defaultEmail]
  );

  useEffect(() => {
    if (!show) return;
    let live = true;
    setLoadState(stateLoading);
    setError(null);
    setNotice(null);
    setBusy(null);

    (async () => {
      try {
        const row = await getTripWeatherSubscription(itemId, groupKey);
        if (!live) return;
        seedFrom(row);
        setLoadState(stateReady);
      } catch (err) {
        if (!live) return;
        setError(extractErrorText(err));
        setLoadState(stateFailed);
      }
    })();

    return () => {
      live = false;
    };
    // Keyed on `show` (and the trip) alone: a re-seed while the user is typing
    // would overwrite what they are typing. Same rule as RenameTripModal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, itemId, groupKey]);

  const trimmedEmail = email.trim();
  const canSchedule = trimmedEmail !== '' && startDate !== '' && stopDate !== '';
  const windowInverted = startDate !== '' && stopDate !== '' && stopDate < startDate;

  async function run(kind, work) {
    if (busy) return;
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (err) {
      setError(extractErrorText(err));
    } finally {
      setBusy(null);
    }
  }

  const payload = () => ({
    itemId,
    groupKey,
    tripName: group?.name ?? '',
    email: trimmedEmail,
    timeZone,
    sendHourLocal: Number(sendHour),
    startDate,
    stopDate,
  });

  function handleSave() {
    return run('save', async () => {
      if (subscription) {
        const saved = await updateTripWeatherSubscription({ id: subscription.id, ...payload() });
        setSubscription(saved);
        setNotice('Schedule updated.');
      } else {
        const created = await createTripWeatherSubscription({
          ...payload(),
          unsubscribeKey: randomId(),
          // Left unset on purpose: no next-send date means "has never sent", and
          // the daemon reads that as "send on the first run inside the window".
          lastSendStatus: 'ok',
        });
        setSubscription(created);
        setNotice('Scheduled.');
      }
    });
  }

  function handleUnschedule() {
    return run('unschedule', async () => {
      await deleteTripWeatherSubscription(subscription.id);
      setSubscription(null);
      const window = defaultSendWindow(group);
      setStartDate(window.startDate ?? '');
      setStopDate(window.stopDate ?? '');
      setNotice('No more weather emails will be sent for this trip.');
    });
  }

  // Deliberately does not save first. See the header.
  function handleSendNow() {
    return run('send', async () => {
      const result = await sendTripWeatherEmailNow({
        itemId,
        groupKey,
        email: trimmedEmail,
      });
      setNotice(
        typeof result === 'string' && result
          ? result
          : `Queued for ${trimmedEmail}. It should arrive in a minute or two.`
      );
    });
  }

  function handleHide() {
    if (busy) return;
    onHide();
  }

  const dirty =
    subscription != null &&
    (subscription.email !== trimmedEmail ||
      (subscription.sendHourLocal ?? DEFAULT_SEND_HOUR) !== Number(sendHour) ||
      (subscription.startDate ?? '') !== startDate ||
      (subscription.stopDate ?? '') !== stopDate);

  return (
    <Modal show={show} onHide={handleHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Weather Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadState === stateLoading && <p className="mb-0">Loading...</p>}

        {loadState === stateFailed && (
          <p className="text-danger mb-0" role="alert">{error}</p>
        )}

        {loadState === stateReady && (
          <>
            <p className="text-muted small">
              A daily briefing for this trip: marine area forecasts and warnings for the
              waters it crosses, conditions now, and the tides and currents where it goes.
            </p>

            <Form.Group className="mb-3" controlId="weather-email-address">
              <Form.Label>Send to</Form.Label>
              <Form.Control
                type="email"
                value={email}
                disabled={Boolean(busy)}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="weather-email-hour">
              <Form.Label>Send at</Form.Label>
              <Form.Select
                value={sendHour}
                disabled={Boolean(busy)}
                onChange={(event) => setSendHour(Number(event.target.value))}
              >
                {HOURS.map((hour) => (
                  <option key={hour} value={hour}>{formatLocalHour(hour)}</option>
                ))}
              </Form.Select>
              <Form.Text>
                {timeZone} &mdash; the same clock through daylight saving.
              </Form.Text>
            </Form.Group>

            <div className="row">
              <Form.Group className="col mb-3" controlId="weather-email-start">
                <Form.Label>Start sending</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  disabled={Boolean(busy)}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </Form.Group>
              <Form.Group className="col mb-3" controlId="weather-email-stop">
                <Form.Label>Stop sending</Form.Label>
                <Form.Control
                  type="date"
                  value={stopDate}
                  disabled={Boolean(busy)}
                  onChange={(event) => setStopDate(event.target.value)}
                />
              </Form.Group>
            </div>

            {windowInverted && (
              <p className="text-danger small" role="alert">
                The stop date is before the start date, so nothing would be sent.
              </p>
            )}

            {subscription && (
              <p className="small mb-1">{describeSchedule(subscription)}</p>
            )}

            {subscription?.nextSendLocalDate && (
              <p className="text-muted small mb-1">
                Next: {describeDay(subscription.nextSendLocalDate)}
              </p>
            )}

            {subscription?.lastSendStatus && subscription.lastSendStatus !== 'ok' && (
              <p className="text-warning small mb-1" role="alert">
                {subscription.lastSendStatus === 'trip-not-found'
                  ? 'The last send could not find this trip, so sending is paused.'
                  : `The last send failed: ${subscription.lastSendStatus}`}
              </p>
            )}

            {notice && <p className="text-success small mb-0" role="status">{notice}</p>}
            {error && <p className="text-danger small mb-0" role="alert">{error}</p>}
          </>
        )}
      </Modal.Body>

      {loadState === stateReady && (
        <Modal.Footer className="justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={handleSendNow}
            disabled={Boolean(busy) || trimmedEmail === ''}
            title="Sends the email now without changing the schedule."
          >
            {busy === 'send' ? 'Sending...' : 'Send now'}
          </Button>

          <div className="d-flex gap-2">
            {subscription && (
              <Button
                variant="outline-danger"
                onClick={handleUnschedule}
                disabled={Boolean(busy)}
              >
                {busy === 'unschedule' ? 'Removing...' : 'Unschedule'}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={Boolean(busy) || !canSchedule || windowInverted || (subscription && !dirty)}
            >
              {busy === 'save'
                ? 'Saving...'
                : subscription
                  ? 'Update'
                  : 'Schedule'}
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default WeatherEmailModal;
